import React, { useState, useEffect } from 'react';
import { Formik, Form, FormikHelpers, useFormikContext } from 'formik';
import { Card, Button, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { object } from 'yup';

import { ResetForm, ServerError } from '@components/Helper';
import { FormGroupControl } from '@components/Base';
import { selectCurrentUser } from '@components/Auth';
import { UserOtpCodeFormGroup } from '@components/Feature/User';
import { yupMobileNumber, yupOtpCode } from '@utils/yup';
import { UserStatuses } from '@models/UserProfile';

import { selectMobileNumberPage } from './selector';
import { mobileNumberUpdate, UpdateMobileNumberPayload } from './action';

type FormValues = UpdateMobileNumberPayload;

const GROUP_PROPS = { className: 'mb-3' };

const FormForm = () => {
  const { t } = useTranslation();
  const { serverErrors, isSuccess } = useSelector(selectMobileNumberPage);
  const { values, isSubmitting } = useFormikContext<FormValues>();

  return (
    <Form>
      <FormGroupControl
        type="tel"
        name="mobileNumber"
        label={t('user.mobileNumber')}
        groupProps={GROUP_PROPS}
      />
      <UserOtpCodeFormGroup mobileNumber={values.mobileNumber} />
      <Button type="submit" disabled={isSubmitting}>
        {t('common:update')}
      </Button>
      <ServerError errors={serverErrors} />
      <ResetForm reset={isSuccess} />
    </Form>
  )
}

const MobileNumberPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const { isSuccess } = useSelector(selectMobileNumberPage);
  const [shouldRenderAlert, setShouldRenderAlert] = useState(isSuccess);

  useEffect(() => {
    setShouldRenderAlert(isSuccess);
  }, [isSuccess]);

  if (!currentUser) return null;
  const { status, mobileNumber } = currentUser;

  const validationSchema = object({
    mobileNumber: yupMobileNumber(),
    otpCode: yupOtpCode(),
  });

  const initialValues: FormValues = {
    mobileNumber,
    otpCode: '',
  };

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const payload: UpdateMobileNumberPayload = values;
    dispatch(mobileNumberUpdate(payload));
    setSubmitting(false);
  };

  return (
    <section>
      <h5 className="mb-4">{t('user.mobileNumber')}</h5>
      {shouldRenderAlert && (
        <Alert variant="success" onClose={() => setShouldRenderAlert(false)} dismissible>
          Your mobile number has been updated. You can sign in with it the next time.
        </Alert>
      )}
      <Card>
        <Card.Body>
          {status === UserStatuses.Confirmed && !!mobileNumber ? (
            <Card.Text>{mobileNumber}</Card.Text>
          ) : (
            <>
              <Alert>
                For your first-time sign-in, please enter your phone number and click ‘Send OTP.’ Then, check your phone for the 4-digit code, enter it, and click ‘Update’.
              </Alert>
              <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                <FormForm />
              </Formik>
            </>
          )}
        </Card.Body>
      </Card>
    </section >
  );
};

export { MobileNumberPage };
